import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import {
  approveZipSubmission,
  batchApproveZipSubmissions,
  fetchZipSubmissions,
  rejectZipSubmission,
} from '@src/api/actions/dashboard/zipSubmissions';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';
import useParentCompanyAutocomplete from '@src/hooks/useParentCompanyAutocomplete';
import paths from '@src/paths';
import PushToFacebookModal from './PushToFacebookModal';

export default function ZipSubmissionsPage() {
  // Parent company filter
  const {
    options: parentCompanyOptions,
    findOptionByValue: findParentCompanyByValue,
  } = useParentCompanyAutocomplete();
  const [filterParentCompanyId, setFilterParentCompanyId] = useState('');

  const {
    options: clientOptions,
    handleInputChange: handleClientSearch,
    findOptionByValue,
  } = useClientAutocomplete({
    parentCompanyId: filterParentCompanyId || undefined,
  });

  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Filters
  const [filterClientId, setFilterClientId] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending');

  // Expanded rows — pending auto-expanded
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [manualCollapsed, setManualCollapsed] = useState(new Set());

  // Sorting
  const [sortField, setSortField] = useState(''); // '', 'pct_changed', 'net_pop', 'days_pending'
  const [sortDir, setSortDir] = useState('desc');

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkApproving, setBulkApproving] = useState(false);

  // Reject dialog
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectSubmissionId, setRejectSubmissionId] = useState(null);
  const [rejectNotes, setRejectNotes] = useState('');
  const [rejectSaving, setRejectSaving] = useState(false);

  // Action loading
  const [actionLoading, setActionLoading] = useState(null);

  // Push to Facebook modal
  const [pushModalOpen, setPushModalOpen] = useState(false);
  const [pushSubmissionId, setPushSubmissionId] = useState(null);
  const [pushBatchQueue, setPushBatchQueue] = useState([]); // queued submission IDs for batch push

  const openPushModal = (submissionId) => {
    setPushBatchQueue([]);
    setPushSubmissionId(submissionId);
    setPushModalOpen(true);
  };

  const handlePushModalClose = () => {
    setPushModalOpen(false);
    // Advance batch queue if present
    if (pushBatchQueue.length > 0) {
      const [next, ...rest] = pushBatchQueue;
      setPushBatchQueue(rest);
      setPushSubmissionId(next);
      // Re-open on next tick so Dialog's exit transition completes cleanly
      setTimeout(() => setPushModalOpen(true), 150);
    } else {
      setPushSubmissionId(null);
    }
  };

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const showSnack = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const loadSubmissions = useCallback(async (pageNum = page) => {
    setLoading(true);
    try {
      const params = { page: pageNum };
      if (filterParentCompanyId) params.parent_company_id = filterParentCompanyId;
      if (filterClientId) params.client_id = filterClientId;
      if (filterStatus) params.status = filterStatus;
      const res = await fetchZipSubmissions(params);
      const results = res.data?.results || [];
      setSubmissions(results);
      setTotalCount(res.data?.count || 0);
      setTotalPages(Math.ceil((res.data?.count || 0) / 50));

      // Auto-expand pending rows
      const pendingIds = new Set(
        results.filter((s) => s.status === 'pending').map((s) => s.id)
      );
      setExpandedIds(pendingIds);
      setManualCollapsed(new Set());
      setSelectedIds(new Set());
    } catch {
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  }, [filterParentCompanyId, filterClientId, filterStatus, page]);

  useEffect(() => {
    setPage(1);
    loadSubmissions(1);
  }, [filterParentCompanyId, filterClientId, filterStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadSubmissions(page);
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  // Quick stats
  const stats = useMemo(() => {
    const pending = submissions.filter((s) => s.status === 'pending').length;
    const approved = submissions.filter((s) => s.status === 'approved').length;
    const rejected = submissions.filter((s) => s.status === 'rejected').length;
    return { pending, approved, rejected, total: totalCount };
  }, [submissions, totalCount]);

  const pendingSubmissions = useMemo(
    () => submissions.filter((s) => s.status === 'pending'),
    [submissions]
  );

  // Batch grouping — detect submissions sharing a batch_id
  const pendingBatches = useMemo(() => {
    const batches = {};
    pendingSubmissions.forEach((s) => {
      if (s.batch_id) {
        if (!batches[s.batch_id]) batches[s.batch_id] = [];
        batches[s.batch_id].push(s);
      }
    });
    // Only return batches with 2+ submissions
    return Object.fromEntries(Object.entries(batches).filter(([, subs]) => subs.length > 1));
  }, [pendingSubmissions]);

  // Sorted submissions
  const sortedSubmissions = useMemo(() => {
    if (!sortField) return submissions;
    const sorted = [...submissions].sort((a, b) => {
      let aVal = 0;
      let bVal = 0;
      if (sortField === 'pct_changed') {
        aVal = a.impact_summary?.pct_zips_changed || 0;
        bVal = b.impact_summary?.pct_zips_changed || 0;
      } else if (sortField === 'net_pop') {
        aVal = a.impact_summary?.net_population_change || 0;
        bVal = b.impact_summary?.net_population_change || 0;
      } else if (sortField === 'days_pending') {
        aVal = new Date(a.submitted_at).getTime();
        bVal = new Date(b.submitted_at).getTime();
      }
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });
    return sorted;
  }, [submissions, sortField, sortDir]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        setManualCollapsed((mc) => new Set([...mc, id]));
      } else {
        next.add(id);
        setManualCollapsed((mc) => {
          const nm = new Set(mc);
          nm.delete(id);
          return nm;
        });
      }
      return next;
    });
  };

  const isExpanded = (id) => expandedIds.has(id) && !manualCollapsed.has(id);

  // ─── Selection ─────────────────────────────────────────────────────

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === pendingSubmissions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pendingSubmissions.map((s) => s.id)));
    }
  };

  // ─── Actions ───────────────────────────────────────────────────────

  const handleApprove = async (submissionId) => {
    if (!window.confirm('Approve this submission? ZIP changes will be applied immediately.')) {
      return;
    }
    setActionLoading(submissionId);
    try {
      await approveZipSubmission(submissionId);
      showSnack('Submission approved. ZIP changes applied and downstream tasks queued.');
      loadSubmissions(page);
      openPushModal(submissionId);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to approve.';
      showSnack(detail, 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleBulkApprove = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    if (!window.confirm(`Approve ${ids.length} submission${ids.length > 1 ? 's' : ''}? ZIP changes will be applied immediately.`)) {
      return;
    }
    setBulkApproving(true);
    let successCount = 0;
    let failCount = 0;
    for (const id of ids) {
      try {
        await approveZipSubmission(id);
        successCount += 1;
      } catch {
        failCount += 1;
      }
    }
    setBulkApproving(false);
    if (failCount > 0) {
      showSnack(`${successCount} approved, ${failCount} failed.`, 'warning');
    } else {
      showSnack(`${successCount} submission${successCount > 1 ? 's' : ''} approved.`);
    }
    loadSubmissions(page);
  };

  const handleBatchApprove = async (batchId) => {
    const batchSubs = pendingBatches[batchId] || [];
    if (!window.confirm(`Approve all ${batchSubs.length} submissions in this batch? ZIP changes will be applied immediately.`)) {
      return;
    }
    setBulkApproving(true);
    try {
      await batchApproveZipSubmissions(batchId);
      showSnack(`Batch of ${batchSubs.length} submissions approved.`);
      loadSubmissions(page);
      // Collapse multi-submission batch into one push per (client_id, vertical_id).
      // Since the push pulls full ClientZipVertical state, one representative
      // submission per unique vertical is enough.
      const seen = new Set();
      const representatives = [];
      for (const s of batchSubs) {
        const key = `${s.client_id || s.client?.id}:${s.vertical_id || s.vertical?.id}`;
        if (seen.has(key)) continue;
        seen.add(key);
        representatives.push(s.id);
      }
      if (representatives.length > 0) {
        const [first, ...rest] = representatives;
        setPushBatchQueue(rest);
        setPushSubmissionId(first);
        setPushModalOpen(true);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to approve batch.';
      showSnack(detail, 'error');
    } finally {
      setBulkApproving(false);
    }
  };

  const handleRejectOpen = (submissionId) => {
    setRejectSubmissionId(submissionId);
    setRejectNotes('');
    setRejectDialogOpen(true);
  };

  const handleRejectSubmit = async () => {
    setRejectSaving(true);
    try {
      await rejectZipSubmission(rejectSubmissionId, {
        reviewer_notes: rejectNotes,
      });
      setRejectDialogOpen(false);
      showSnack('Submission rejected.');
      loadSubmissions(page);
    } catch {
      showSnack('Failed to reject.', 'error');
    } finally {
      setRejectSaving(false);
    }
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="h4">ZIP Submissions</Typography>
          {stats.pending > 0 && (
            <Chip
              label={`${stats.pending} pending`}
              color="warning"
              size="small"
            />
          )}
        </Stack>
      </Box>

      {/* ─── Quick Stats ──────────────────────────────────────────────── */}
      <Stack direction="row" spacing={2} mb={2}>
        <StatChip label="Total" value={stats.total} />
        <StatChip label="Pending" value={stats.pending} color="warning" />
        <StatChip label="Approved" value={stats.approved} color="success" />
        <StatChip label="Rejected" value={stats.rejected} color="error" />
      </Stack>

      <Card>
        <CardContent>
          {/* Filters */}
          <Stack direction="row" spacing={2} mb={2} alignItems="center" flexWrap="wrap">
            {parentCompanyOptions.length > 0 && (
              <Autocomplete
                options={parentCompanyOptions}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                value={findParentCompanyByValue(filterParentCompanyId)}
                onChange={(_, val) => {
                  setFilterParentCompanyId(val ? String(val.value) : '');
                  setFilterClientId('');
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Parent Company" size="small" sx={{ minWidth: 220 }} />
                )}
              />
            )}
            <Autocomplete
              options={clientOptions}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              value={findOptionByValue(filterClientId)}
              onChange={(_, val) => setFilterClientId(val ? String(val.value) : '')}
              onInputChange={handleClientSearch}
              renderInput={(params) => (
                <TextField {...params} label="Client" size="small" sx={{ minWidth: 220 }} />
              )}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
            <Button variant="outlined" onClick={() => loadSubmissions(1)} disabled={loading}>
              {loading ? <CircularProgress size={18} /> : 'Refresh'}
            </Button>

            {/* Bulk approve */}
            {selectedIds.size > 0 && (
              <Button
                variant="contained"
                color="success"
                onClick={handleBulkApprove}
                disabled={bulkApproving}
                startIcon={bulkApproving ? <CircularProgress size={16} /> : null}
              >
                Approve {selectedIds.size} Selected
              </Button>
            )}
          </Stack>

          {loading ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          ) : submissions.length === 0 ? (
            <Alert severity="info">No submissions found.</Alert>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        {pendingSubmissions.length > 0 && (
                          <Checkbox
                            size="small"
                            checked={selectedIds.size === pendingSubmissions.length && pendingSubmissions.length > 0}
                            indeterminate={selectedIds.size > 0 && selectedIds.size < pendingSubmissions.length}
                            onChange={toggleSelectAll}
                          />
                        )}
                      </TableCell>
                      <TableCell width={40} />
                      <TableCell>Client</TableCell>
                      <TableCell>Territory</TableCell>
                      <TableCell>Vertical</TableCell>
                      <TableCell>Adds</TableCell>
                      <TableCell>Removes</TableCell>
                      <SortableHeader field="net_pop" label="Net Pop" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                      <SortableHeader field="pct_changed" label="% Changed" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                      <TableCell>Submitted By</TableCell>
                      <SortableHeader field="days_pending" label="Submitted" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedSubmissions.map((sub) => {
                      const expanded = isExpanded(sub.id);
                      const isPending = sub.status === 'pending';
                      const isSelected = selectedIds.has(sub.id);
                      const impact = sub.impact_summary;
                      const pctChanged = impact?.pct_zips_changed || 0;
                      const netPop = impact?.net_population_change || 0;
                      const isHighImpact = pctChanged > 20;
                      const daysPending = isPending ? getDaysPending(sub.submitted_at) : null;

                      return [
                        <TableRow
                          key={sub.id}
                          hover
                          sx={isPending ? { bgcolor: 'warning.50' } : undefined}
                        >
                          <TableCell padding="checkbox">
                            {isPending && (
                              <Checkbox
                                size="small"
                                checked={isSelected}
                                onChange={() => toggleSelect(sub.id)}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <IconButton size="small" onClick={() => toggleExpand(sub.id)}>
                              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <span>{sub.client_name}</span>
                              {isHighImpact && (
                                <Tooltip title={`High impact: ${pctChanged}% of territory changing`}>
                                  <WarningAmberIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                                </Tooltip>
                              )}
                              <Tooltip title="View client ZIP coverage">
                                <IconButton
                                  size="small"
                                  href={`${paths.zipCoverage}?client_id=${sub.client}`}
                                  target="_blank"
                                >
                                  <OpenInNewIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                          <TableCell>{sub.territory_name || '-'}</TableCell>
                          <TableCell>{sub.vertical_name}</TableCell>
                          <TableCell>
                            <Chip label={sub.add_count} size="small" color="success" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Chip label={sub.remove_count} size="small" color="error" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            {netPop !== 0 ? (
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                color={netPop > 0 ? 'success.main' : 'error.main'}
                              >
                                {netPop > 0 ? '+' : ''}{netPop.toLocaleString()}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              color={isHighImpact ? 'warning.main' : 'text.primary'}
                            >
                              {pctChanged}%
                            </Typography>
                          </TableCell>
                          <TableCell>{sub.submitted_by_email}</TableCell>
                          <TableCell>
                            <Stack spacing={0}>
                              <DaysPendingLabel daysPending={daysPending} submittedAt={sub.submitted_at} />
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Chip
                                label={sub.status}
                                size="small"
                                color={
                                  sub.status === 'pending' ? 'warning'
                                  : sub.status === 'approved' ? 'success'
                                  : sub.status === 'rejected' ? 'error'
                                  : 'default'
                                }
                              />
                              {sub.batch_id && pendingBatches[sub.batch_id] && (
                                <Chip label="batch" size="small" variant="outlined" />
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell align="right">
                            {isPending && (
                              <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                                {sub.batch_id && pendingBatches[sub.batch_id] && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="success"
                                    onClick={() => handleBatchApprove(sub.batch_id)}
                                    disabled={bulkApproving}
                                  >
                                    Approve Batch ({pendingBatches[sub.batch_id].length})
                                  </Button>
                                )}
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="success"
                                  onClick={() => handleApprove(sub.id)}
                                  disabled={actionLoading === sub.id}
                                >
                                  {actionLoading === sub.id ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    'Approve'
                                  )}
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  onClick={() => handleRejectOpen(sub.id)}
                                >
                                  Reject
                                </Button>
                              </Stack>
                            )}
                            {sub.status === 'approved' && (
                              <Stack
                                direction="row"
                                spacing={1}
                                justifyContent="flex-end"
                                alignItems="center"
                              >
                                {sub.reviewed_by_email && (
                                  <Typography variant="caption" color="text.secondary">
                                    by {sub.reviewed_by_email}
                                  </Typography>
                                )}
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => openPushModal(sub.id)}
                                >
                                  Push FB
                                </Button>
                              </Stack>
                            )}
                          </TableCell>
                        </TableRow>,

                        /* Expanded row with items */
                        <TableRow key={`${sub.id}-detail`}>
                          <TableCell colSpan={14} sx={{ p: 0, border: expanded ? undefined : 'none' }}>
                            <Collapse in={expanded}>
                              <Box p={2} bgcolor="action.hover">
                                {sub.submitter_notes && (
                                  <Alert severity="info" variant="outlined" sx={{ mb: 1.5 }}>
                                    Client note: {sub.submitter_notes}
                                  </Alert>
                                )}
                                {sub.reviewer_notes && (
                                  <Alert severity="info" sx={{ mb: 1.5 }}>
                                    Reviewer note: {sub.reviewer_notes}
                                  </Alert>
                                )}
                                {sub.impact_summary && (
                                  <ImpactSummary impact={sub.impact_summary} addCount={sub.add_count} removeCount={sub.remove_count} />
                                )}
                                <Stack direction="row" spacing={4}>
                                  <Box>
                                    {(() => {
                                      const adds = (sub.items || []).filter((i) => i.action === 'add');
                                      const addPop = adds.reduce((s, i) => s + (i.population || 0), 0);
                                      return (
                                        <Typography variant="subtitle2" gutterBottom>
                                          Adds ({adds.length}){addPop > 0 ? ` \u2022 ${addPop.toLocaleString()} pop.` : ''}
                                        </Typography>
                                      );
                                    })()}
                                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                                      {(sub.items || [])
                                        .filter((i) => i.action === 'add')
                                        .map((item) => (
                                          <Tooltip key={item.id} title={item.city && item.state ? `${item.city}, ${item.state}` : ''} arrow>
                                            <Chip
                                              label={item.zip || item.zip_raw}
                                              size="small"
                                              color="success"
                                              variant="outlined"
                                            />
                                          </Tooltip>
                                        ))}
                                      {(sub.items || []).filter((i) => i.action === 'add').length === 0 && (
                                        <Typography variant="body2" color="text.secondary">None</Typography>
                                      )}
                                    </Box>
                                  </Box>
                                  <Box>
                                    {(() => {
                                      const removes = (sub.items || []).filter((i) => i.action === 'remove');
                                      const removePop = removes.reduce((s, i) => s + (i.population || 0), 0);
                                      return (
                                        <Typography variant="subtitle2" gutterBottom>
                                          Removes ({removes.length}){removePop > 0 ? ` \u2022 ${removePop.toLocaleString()} pop.` : ''}
                                        </Typography>
                                      );
                                    })()}
                                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                                      {(sub.items || [])
                                        .filter((i) => i.action === 'remove')
                                        .map((item) => (
                                          <Tooltip key={item.id} title={item.city && item.state ? `${item.city}, ${item.state}` : ''} arrow>
                                            <Chip
                                              label={item.zip || item.zip_raw}
                                              size="small"
                                              color="error"
                                              variant="outlined"
                                            />
                                          </Tooltip>
                                        ))}
                                      {(sub.items || []).filter((i) => i.action === 'remove').length === 0 && (
                                        <Typography variant="body2" color="text.secondary">None</Typography>
                                      )}
                                    </Box>
                                  </Box>
                                </Stack>
                                {sub.reviewed_at && (
                                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                                    Reviewed {new Date(sub.reviewed_at).toLocaleDateString()} by {sub.reviewed_by_email || 'staff'}
                                  </Typography>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>,
                      ];
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box display="flex" justifyContent="center" mt={2}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, v) => setPage(v)}
                />
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* Reject Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Submission</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label="Reason / Notes (visible to client)"
            value={rejectNotes}
            onChange={(e) => setRejectNotes(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRejectSubmit}
            disabled={rejectSaving}
          >
            {rejectSaving ? <CircularProgress size={18} /> : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Push to Facebook Modal */}
      <PushToFacebookModal
        open={pushModalOpen}
        onClose={handlePushModalClose}
        submissionId={pushSubmissionId}
        onPushComplete={() => {
          showSnack('Push to Facebook complete.');
          loadSubmissions(page);
        }}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function StatChip({ label, value, color = 'default' }) {
  return (
    <Chip
      label={`${label}: ${value}`}
      size="small"
      color={color}
      variant={color === 'default' ? 'outlined' : 'filled'}
      sx={{ fontWeight: 600 }}
    />
  );
}

function getDaysPending(submittedAt) {
  const now = new Date();
  const submitted = new Date(submittedAt);
  return Math.floor((now - submitted) / (1000 * 60 * 60 * 24));
}

function DaysPendingLabel({ daysPending, submittedAt }) {
  const date = new Date(submittedAt);
  if (daysPending === null) {
    return (
      <Typography variant="body2">
        {date.toLocaleDateString()}{' '}
        <Typography component="span" variant="caption" color="text.secondary">
          {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </Typography>
      </Typography>
    );
  }

  let color = 'text.secondary';
  if (daysPending >= 7) color = 'error.main';
  else if (daysPending >= 3) color = 'warning.main';

  const label = daysPending === 0 ? 'Today' : daysPending === 1 ? '1d ago' : `${daysPending}d ago`;

  return (
    <>
      <Typography variant="body2">
        {date.toLocaleDateString()}
      </Typography>
      <Typography variant="caption" fontWeight={600} color={color}>
        {label}
      </Typography>
    </>
  );
}

function SortableHeader({ field, label, sortField, sortDir, onSort }) {
  const active = sortField === field;
  return (
    <TableCell
      sx={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
      onClick={() => onSort(field)}
    >
      <Stack direction="row" spacing={0.5} alignItems="center">
        <span>{label}</span>
        {active ? (
          sortDir === 'asc' ? (
            <ArrowUpwardIcon sx={{ fontSize: 14 }} />
          ) : (
            <ArrowDownwardIcon sx={{ fontSize: 14 }} />
          )
        ) : (
          <ArrowDownwardIcon sx={{ fontSize: 14, opacity: 0.25 }} />
        )}
      </Stack>
    </TableCell>
  );
}

function ImpactSummary({ impact, addCount, removeCount }) {
  const {
    current_zip_count,
    current_population,
    after_zip_count,
    after_population,
    add_population,
    remove_population,
    net_population_change,
    pct_zips_changed,
  } = impact;

  const fmt = (n) => (n || 0).toLocaleString();
  const netZips = addCount - removeCount;
  const netZipSign = netZips >= 0 ? '+' : '';
  const netPopSign = net_population_change >= 0 ? '+' : '';

  return (
    <Box
      sx={{
        mb: 1.5,
        p: 1.5,
        bgcolor: 'background.paper',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
        Impact Summary
      </Typography>
      <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
        {/* ZIP count before → after */}
        <Box>
          <Typography variant="caption" color="text.secondary">ZIPs</Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="body2" fontWeight={600}>{fmt(current_zip_count)}</Typography>
            <ArrowForwardIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="body2" fontWeight={600}>{fmt(after_zip_count)}</Typography>
            <Typography
              variant="body2"
              color={netZips > 0 ? 'success.main' : netZips < 0 ? 'error.main' : 'text.secondary'}
              sx={{ ml: 0.5 }}
            >
              ({netZipSign}{fmt(netZips)})
            </Typography>
          </Stack>
        </Box>

        {/* Population before → after */}
        <Box>
          <Typography variant="caption" color="text.secondary">Population</Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="body2" fontWeight={600}>{fmt(current_population)}</Typography>
            <ArrowForwardIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="body2" fontWeight={600}>{fmt(after_population)}</Typography>
            <Typography
              variant="body2"
              color={net_population_change > 0 ? 'success.main' : net_population_change < 0 ? 'error.main' : 'text.secondary'}
              sx={{ ml: 0.5 }}
            >
              ({netPopSign}{fmt(net_population_change)})
            </Typography>
          </Stack>
        </Box>

        {/* Add / Remove population breakdown */}
        <Box>
          <Typography variant="caption" color="text.secondary">Add Pop / Remove Pop</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="success.main" fontWeight={600}>+{fmt(add_population)}</Typography>
            <Typography variant="body2" color="text.secondary">/</Typography>
            <Typography variant="body2" color="error.main" fontWeight={600}>-{fmt(remove_population)}</Typography>
          </Stack>
        </Box>

        {/* % territory changed */}
        <Box>
          <Typography variant="caption" color="text.secondary">Territory Changed</Typography>
          <Typography
            variant="body2"
            fontWeight={600}
            color={pct_zips_changed > 20 ? 'warning.main' : 'text.primary'}
          >
            {pct_zips_changed}%
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}
